import { useState } from 'react';
import { X, Check, Edit2, Trash2 } from 'lucide-react';
import type { CommentData } from '../nodes/CommentNode';

interface CommentPopoverProps {
  position: { x: number; y: number };
  commentData: CommentData;
  onUpdateComment: (newContent: string) => void;
  onResolveComment: () => void;
  onDeleteComment: () => void;
  onClose: () => void;
}

export function CommentPopover({
  position,
  commentData,
  onUpdateComment,
  onResolveComment,
  onDeleteComment,
  onClose,
}: CommentPopoverProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(commentData.content);

  const handleSaveEdit = () => {
    if (editContent.trim() !== commentData.content) {
      onUpdateComment(editContent.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditContent(commentData.content);
    setIsEditing(false);
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div
      className="fixed z-50 bg-white border border-gray-300 rounded-lg shadow-lg max-w-sm"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
            {commentData.author.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">{commentData.author}</div>
            <div className="text-xs text-gray-500">{formatTimestamp(commentData.timestamp)}</div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="p-3">
        {isEditing ? (
          <div className="space-y-3">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Edit your comment..."
            />
            <div className="flex gap-2">
              <button
                onClick={handleSaveEdit}
                className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
              >
                Save
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-900 leading-relaxed">{commentData.content}</p>

            {/* Actions */}
            <div className="flex gap-2">
              {!commentData.resolved && (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                  >
                    <Edit2 className="w-3 h-3" />
                    Edit
                  </button>
                  <button
                    onClick={onResolveComment}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                  >
                    <Check className="w-3 h-3" />
                    Resolve
                  </button>
                  <button
                    onClick={onDeleteComment}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                </>
              )}
              {commentData.resolved && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 px-2 py-1 text-xs text-green-600 bg-green-50 rounded">
                    <Check className="w-3 h-3" />
                    Resolved
                  </div>
                  <button
                    onClick={onDeleteComment}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}